/**
 * ContextMenuArea (DESIGN.md §18): one context menu for all items inside it. Items carry
 * `data-context-id`; the menu content comes from the action registry via `menuFor`.
 *
 * Ways to open it, all with the same actions:
 * - right-click (pointer position),
 * - Shift+F10 or the context-menu key on the focused item (`app.contextMenu` in the keymap),
 * - long-press on touch: a dropdown at the finger on tablets, the action sheet on phones (< md).
 *
 * The browser's own menu stays available where it is useful: in text fields and editors, on
 * text the user has selected and with Shift+right-click (`prefersNativeMenu`).
 *
 * Implementation note: the menu is a `DropdownMenu` anchored at a virtual point, not Radix
 * `ContextMenu`, because that one cannot be opened from the keyboard on a virtualized listbox
 * (focus stays on the list) or swapped for a sheet on phones. One menu renderer serves the "…"
 * overflow button and the context menu.
 */
import { useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../components/dropdown-menu';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { useLongPress } from '../hooks/use-long-press';
import { getShortcuts, isTypingTarget, matchesChord } from '../hooks/use-shortcut';
import { useUiStrings } from '../strings';
import { ActionMenuItems, ActionSheet } from './action-menu';
import { prefersNativeMenu, type ResolvedAction } from './registry';

/** Attribute that marks an item of a `ContextMenuArea` (value = item id). */
export const CONTEXT_ID_ATTRIBUTE = 'data-context-id';

export interface ContextMenuSpec<T = unknown> {
  /** Names the target (subject, "3 messages"): menu label and sheet title. */
  title?: string;
  actions: readonly ResolvedAction<T>[];
}

export interface ContextMenuAreaProps {
  /**
   * Menu for the item with this id. Lists apply the selection rule here (`contextTargets`).
   * Returning undefined (or no actions) leaves the browser's menu; no function = no context menu.
   */
  menuFor: ((id: string) => ContextMenuSpec | undefined) | undefined;
  /** Item for the keyboard when focus is on the container (listbox with `aria-activedescendant`). */
  focusedId?: () => string | undefined;
  /** A plain click on an item also opens its menu (items without an own click action, e.g. addresses). */
  openOnClick?: boolean;
  children: ReactNode;
}

interface OpenMenu {
  spec: ContextMenuSpec;
  x: number;
  y: number;
  sheet: boolean;
  restoreFocus: HTMLElement | null;
  key: number;
}

/** A native `contextmenu` right after a keyboard open is the same gesture (some browsers send both). */
const KEYBOARD_ECHO_MS = 400;

const itemOf = (target: EventTarget | null): HTMLElement | null =>
  target instanceof Element ? target.closest<HTMLElement>(`[${CONTEXT_ID_ATTRIBUTE}]`) : null;

export function ContextMenuArea({ menuFor, focusedId, openOnClick = false, children }: ContextMenuAreaProps) {
  const strings = useUiStrings();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const container = useRef<HTMLDivElement>(null);
  const keyboardOpenedAt = useRef(0);
  const [menu, setMenu] = useState<OpenMenu>();

  const findItem = (id: string): HTMLElement | null => {
    for (const element of container.current?.querySelectorAll<HTMLElement>(`[${CONTEXT_ID_ATTRIBUTE}]`) ?? []) {
      if (element.getAttribute(CONTEXT_ID_ATTRIBUTE) === id) return element;
    }
    return null;
  };

  /** Opens the menu of `id`; false when there is none (the caller then leaves the native menu). */
  const open = (id: string, point: { x: number; y: number } | undefined, sheet: boolean): boolean => {
    const spec = menuFor?.(id);
    if (!spec || spec.actions.length === 0) return false;
    // Keyboard and keyboard-like events have no pointer position: anchor at the item.
    const rect = (findItem(id) ?? container.current?.firstElementChild)?.getBoundingClientRect();
    const at = point ?? { x: (rect?.left ?? 0) + 16, y: rect?.bottom ?? 0 };
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setMenu({ spec, ...at, sheet, restoreFocus: focused, key: Date.now() });
    return true;
  };

  const { isPressing, ...pressHandlers } = useLongPress(({ target, x, y }) => {
    const item = itemOf(target);
    const id = item?.getAttribute(CONTEXT_ID_ATTRIBUTE);
    if (id) open(id, { x, y }, !isDesktop);
  });

  const onContextMenu = (event: MouseEvent) => {
    // Same gesture as a long-press (Android) or a keyboard open: already handled.
    if (isPressing() || performance.now() - keyboardOpenedAt.current < KEYBOARD_ECHO_MS) {
      event.preventDefault();
      return;
    }
    if (prefersNativeMenu(event.target, { shiftKey: event.shiftKey, selection: window.getSelection() })) return;
    const id = itemOf(event.target)?.getAttribute(CONTEXT_ID_ATTRIBUTE);
    if (!id) return;
    const fromKeyboard = event.clientX === 0 && event.clientY === 0;
    if (open(id, fromKeyboard ? undefined : { x: event.clientX, y: event.clientY }, false)) event.preventDefault();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!getShortcuts('app.contextMenu').some((chord) => matchesChord(event.nativeEvent, chord))) return;
    if (isTypingTarget(event.target)) return;
    const id = itemOf(event.target)?.getAttribute(CONTEXT_ID_ATTRIBUTE) ?? focusedId?.();
    if (!id) return;
    if (open(id, undefined, false)) {
      event.preventDefault();
      keyboardOpenedAt.current = performance.now();
    }
  };

  const onClick = (event: MouseEvent) => {
    const id = itemOf(event.target)?.getAttribute(CONTEXT_ID_ATTRIBUTE);
    if (id && open(id, undefined, !isDesktop)) event.preventDefault();
  };

  const close = () => setMenu(undefined);
  const label = menu?.spec.title ?? strings.actions;
  // Without actions the area is transparent (optional context menus of composites).
  if (!menuFor) return children;

  return (
    <>
      <div
        ref={container}
        className="contents"
        onContextMenu={onContextMenu}
        onKeyDown={onKeyDown}
        onClick={openOnClick ? onClick : undefined}
        {...pressHandlers}
      >
        {children}
      </div>
      {menu?.sheet && <ActionSheet open onOpenChange={(value) => !value && close()} title={menu.spec.title} actions={menu.spec.actions} />}
      {menu && !menu.sheet && (
        <DropdownMenu key={menu.key} open onOpenChange={(value) => !value && close()}>
          <DropdownMenuTrigger asChild>
            {/* Names the menu: Radix labels the content by its trigger. */}
            <span aria-label={label} className="pointer-events-none fixed size-0" style={{ left: menu.x, top: menu.y }} />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            sideOffset={2}
            onCloseAutoFocus={(event) => {
              // Back to where the user was (the list or the item), not to the virtual anchor.
              event.preventDefault();
              menu.restoreFocus?.focus();
            }}
          >
            <ActionMenuItems actions={menu.spec.actions} />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
